/**
 * which SPA routes a session-less visitor may see, concluded from the backend's
 * controller annotations rather than from docs: a public page's endpoint carries
 * no @Permission and no @LoginRequired, so possession of the URL is the
 * authorization. `/api/optimizer/lot/:1` does carry `@Permission(PERMISSION_VIEW_LOT)`,
 * which is why `/lots/won/:lotId` is PRIVATE despite docs/ROUTES.md listing it
 * with the pub pages.
 *
 * HINT: kept in sync with src/App.svelte by `pnpm lint:pubroutes`, which fails in both
 * directions — a new route nobody classified, and a classification whose route
 * is gone.
 */

/** route patterns as written in src/App.svelte, `:param` segments included */
export const PUBLIC_ROUTES = [
	'/angebot/:requestPublicId/requested/:requestedEntityType/:requestedEntityId/user/:offerUserPublicId',
	'/lots/bid/:requestPublicId/requested/:requestedEntityType/:requestedEntityId/user/:offerUserPublicId',
	'/position/:lotPublicId/gegenangebot/:reqPublicId/user/:offerUserPublicId',
	'/position/:lotPublicId',
	'/404',
	'/401'
] as const

/** listed explicitly rather than inferred as "not public", so a new route fails the audit until somebody classifies it */
export const PRIVATE_ROUTES = [
	'/saas-dashboard',
	'/analysis',
	'/analytic',
	'/catalogs',
	'/catalog/:catalogName/material/:materialId',
	'/corporateDesign',
	'/dashboard',
	'/hlpConfiguration',
	'/hlp',
	'/installationCases',
	'/mailProtocol',
	'/mailTemplates',
	'/manage/tags',
	'/map',
	'/maplibre',
	'/materialProperties',
	'/materialProperty/:materialPropertyId',
	'/materialSubstitutes',
	'/system/matching',
	'/ui/lot/:lotId/accountingMailConfiguration',
	// /api/optimizer/lot/:1 carries @Permission(PERMISSION_VIEW_LOT)
	'/lots/won/:lotId',
	'/quantities'
] as const

/** excluded by instruction — not tested, not classified, never silently dropped */
export const UNCLASSIFIED_ROUTES = ['/dev'] as const

const PARAM_SEGMENT = /:[^/]+/g

/** a `:param` matches one segment only, so a public prefix cannot open a deeper path; trailing slash tolerated because buildCustomerLink emits one */
function patternToRegex(pattern: string): RegExp {
	const escaped = pattern.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&')
	return new RegExp(`^${escaped.replaceAll(PARAM_SEGMENT, '[^/]+')}/?$`)
}

const PUBLIC_MATCHERS = PUBLIC_ROUTES.map(patternToRegex)
const PRIVATE_MATCHERS = PRIVATE_ROUTES.map(patternToRegex)

/**
 * the classification has to see the same route the router will: `//dashboard`
 * matched neither list, so the guard did not fire, and the router then resolved
 * it to `/dashboard` and rendered the page to a visitor with no session
 */
export function normalisePathname(pathname: string): string {
	return pathname.replaceAll(/\/{2,}/g, '/')
}

export function isPublicRoute(pathname: string): boolean {
	const path = normalisePathname(pathname)
	return PUBLIC_MATCHERS.some((matcher) => matcher.test(path))
}

/**
 * deliberately not `!isPublicRoute`: the catch-all `<Route>` has no path, so
 * undeclared URLs reach the shell too, and treating those as private redirected
 * `/login` to itself until ERR_TOO_MANY_REDIRECTS. they are left to the backend,
 * which is only safe because `pnpm lint:pubroutes` fails on any unclassified route
 */
export function isPrivateRoute(pathname: string): boolean {
	const path = normalisePathname(pathname)
	return PRIVATE_MATCHERS.some((matcher) => matcher.test(path))
}
